import { addDays, todayIso } from '@shkola/api';
import { tones } from '@shkola/design';
import { Link } from '@tanstack/react-router';
import { type RefObject, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretRightIcon, MegaphoneIcon } from '../../design/icons';
import { OnColorProvider, StatusPill } from '../../design/ui';
import { gradeShort } from '../../lib/labels';
import { DoneArt } from './Art';
import {
  type TaskGroup,
  gradeTone,
  ground,
  useGradeFeed,
  useSchoolFeed,
  useTaskGroups,
} from './blocks';
import { TodoLinkTo } from './Cards';
import { ON_PEDESTAL, Pedestal } from './GlassArt';
import { EVENT_LOOK, GlassDay } from './GlassDay';
import { CardHead, TaskRows } from './GlassHome';
import { ProfileHeader } from './ProfileHeader';
import { type Sim, Situations, useSim } from './Situations';
import { type StudentContext, dayMonthShort, weekdayShort } from './model';

/*
 * «Скло 2» — «Скло» with his seven edits of 27.10, as a version of its own (so «Скло» stays as it was):
 *  1. no shadows under the blocks;
 *  2. the page scrolls as any page — the browser's bar on the right edge, no invisible plate clipping the
 *     blocks at the bottom (AppLayout, `data-scrolls`);
 *  3–4. blocks of the moment: each is there while its situation is (Situations: an alarm, an
 *     announcement to confirm; here: the new grades, the school) and goes when it is over; the others
 *     close up, nothing breaks — the column beside the lessons is a plain stack, each block as tall as
 *     what it holds; the cards of what to do fill their rows whatever their number (one — one wide card,
 *     odd — the first one wide), no slider of four on a computer;
 *  5. the mood is a button in the header, not a block;
 *  6. the header as a profile (ProfileHeader): a cover, the face with stickers, tags, «Зараз / Далі»;
 *  7. a clean ground.
 * The lessons stay in view while the column beside them scrolls (sticky; a calendar taller than the
 * window scrolls first and then sticks by its bottom).
 */

const BLOCK = 'flex min-w-0 flex-col rounded-[32px] bg-white p-5 md:p-6';

/** The top of the sticky calendar: under the bar, or higher for one taller than the window. */
function useStickyTop(ref: RefObject<HTMLDivElement | null>) {
  const [top, setTop] = useState(72);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const fit = () => setTop(Math.min(72, window.innerHeight - el.offsetHeight - 12));
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', fit);
    };
  }, [ref]);
  return top;
}

// —— what to do: the cards fill their rows whatever their number ——

/**
 * A kind of task as a «Duomo» card: its name and count, its thing on a pedestal, the tasks inside; `wide`
 * — alone in its row: the name and the picture on the left, the tasks on the right.
 */
function TaskCard({
  group,
  wide = false,
  className = '',
}: {
  group: TaskGroup;
  wide?: boolean;
  className?: string;
}) {
  const tint = tones[group.tone];
  const Art = group.art;
  return (
    <div
      className={`relative isolate flex min-w-0 flex-col gap-1.5 overflow-hidden rounded-[24px] p-1.5 ring-2 ring-white ring-inset ${wide ? 'sm:grid sm:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] sm:items-center' : ''} ${className}`}
    >
      <span
        aria-hidden
        className="absolute inset-0 -z-10"
        style={{ backgroundImage: ground(group.tone) }}
      />
      <div className="flex items-start gap-1 pt-2 pl-3">
        <TodoLinkTo link={group.link} className="min-w-0 flex-1 pt-1">
          <span className="block text-[13.5px] leading-5 font-semibold" style={{ color: tint.ink }}>
            {group.title}
          </span>
          {group.count > 0 && (
            <span
              className="block font-display text-[34px] leading-10 font-semibold tracking-[-0.02em]"
              style={{ color: tint.ink }}
            >
              {group.count}
            </span>
          )}
        </TodoLinkTo>
        <span className="relative -mt-1 -mb-2 block h-[78px] w-[100px] shrink-0">
          <Pedestal
            tone={group.tone}
            thing={<Art tone={group.tone} {...ON_PEDESTAL} />}
            className="absolute inset-0 h-full w-full"
          />
        </span>
      </div>
      {group.text ? (
        <p className="mt-auto rounded-[18px] bg-white/85 p-3.5 text-[13.5px] text-ink-soft">
          {group.text}
        </p>
      ) : (
        <TaskRows group={group} max={wide ? 3 : 2} />
      )}
    </div>
  );
}

function TodoBoard({
  ctx,
  sim,
  className = '',
}: {
  ctx: StudentContext;
  sim: ReadonlySet<Sim>;
  className?: string;
}) {
  const { t } = useTranslation();
  const tasks = useTaskGroups(ctx);
  // what to confirm stands on top among the blocks of the moment
  let groups = tasks.groups.filter((g) => g.key !== 'ack');
  if (sim.has('notasks')) groups = [];
  if (sim.has('onetask')) groups = groups.slice(0, 1);
  if (!tasks.pending && groups.length === 0)
    groups = [
      {
        key: 'done',
        tone: 'green',
        art: DoneArt,
        title: t('studentHome.glass.board.done'),
        count: 0,
        rows: [],
        link: { to: '/homework' },
        text: t('studentHome.glass.board.doneText'),
      },
    ];
  const total = groups.filter((g) => g.key !== 'done').reduce((n, g) => n + g.count, 0);
  // an odd number: the most urgent goes wide across the row, the rest in pairs — no empty half
  const odd = groups.length % 2 === 1;
  const keys = groups.map((g) => g.key).join(',');
  const line = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    // the cards come one by one as the data loads: start the line on a phone from the first one again
    if (line.current && keys) line.current.scrollLeft = 0;
  }, [keys]);
  return (
    <section className={`${BLOCK} ${className}`}>
      <CardHead
        title={t('studentHome.glass.todo')}
        count={total}
        to="/homework"
        label={t('studentHome.glass.all')}
      />
      {tasks.pending && groups.length === 0 ? (
        <div className="h-[200px] animate-pulse rounded-[24px] bg-soft" aria-busy />
      ) : (
        // a phone: one line to swipe; a computer: rows of two, nothing slides
        <div
          ref={line}
          className={`no-scrollbar grid snap-x snap-mandatory grid-flow-col gap-2 overflow-x-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible ${groups.length === 1 ? 'auto-cols-[100%]' : 'auto-cols-[84%] sm:auto-cols-auto'}`}
        >
          {groups.map((g, i) => (
            <TaskCard
              key={g.key}
              group={g}
              wide={odd && i === 0}
              className={`snap-start ${odd && i === 0 ? 'sm:col-span-2' : ''}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// —— the new grades: only while there are new ones ——

function NewGrades({
  ctx,
  sim,
  className = '',
}: {
  ctx: StudentContext;
  sim: ReadonlySet<Sim>;
  className?: string;
}) {
  const { t } = useTranslation();
  const { grades, unread } = useGradeFeed(ctx);
  const week = addDays(todayIso(), -7);
  let fresh = grades.filter((g) => unread.has(g.id) || g.date > week);
  if (sim.has('nogrades')) fresh = [];
  if (sim.has('onegrade')) fresh = fresh.slice(0, 1);
  if (fresh.length === 0) return null;
  const shown = fresh.slice(0, 6);
  return (
    <section className={`${BLOCK} ${className}`}>
      <CardHead
        title={t('studentHome.glass.newGrades')}
        count={fresh.length}
        to="/grades"
        label={t('studentHome.glass.allGrades')}
      />
      {/* as many in a row as the width takes (a tile at least 250 px), the last row stretched: whatever
          the number, no empty place */}
      <ul className="flex flex-wrap gap-2">
        {shown.map((g) => {
          const tone = gradeTone(g);
          const tint = tones[tone];
          return (
            <li key={g.id} className="min-w-0 grow basis-[250px]">
              <Link
                to="/grades"
                className="flex h-full min-w-0 items-center gap-3 rounded-[20px] p-2.5 pr-3.5 transition-[filter] hover:brightness-[0.98]"
                style={{ backgroundColor: tint.soft }}
              >
                <span
                  className="flex size-12 shrink-0 items-center justify-center rounded-[15px] bg-white font-display text-[22px] font-semibold"
                  style={{ color: tint.vivid }}
                >
                  {gradeShort(t, g.value, g.special)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate text-[14px] leading-5 font-semibold text-ink">
                      {g.subject}
                    </span>
                    {unread.has(g.id) && (
                      <OnColorProvider value>
                        <StatusPill tone={tone} size="xs">
                          {t('studentHome.glass.fresh')}
                        </StatusPill>
                      </OnColorProvider>
                    )}
                  </span>
                  <span
                    className="block truncate text-[12.5px] leading-[18px]"
                    style={{ color: tint.ink }}
                  >
                    {g.comment
                      ? `«${g.comment}»`
                      : (g.title ?? g.topic ?? t(`journal.kinds.${g.kind}`))}
                    {' · '}
                    {dayMonthShort(g.date)}
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {fresh.length > shown.length && (
        <Link
          to="/grades"
          className="mt-3 inline-flex items-center gap-1 self-start text-[13px] font-semibold text-tone-blue-vivid hover:underline"
        >
          {t('studentHome.glass2.grades.more', { count: fresh.length - shown.length })}
          <CaretRightIcon size={11} weight="bold" aria-hidden />
        </Link>
      )}
    </section>
  );
}

// —— the school: only while there is news or an event near ——

function School({
  ctx,
  sim,
  className = '',
}: {
  ctx: StudentContext;
  sim: ReadonlySet<Sim>;
  className?: string;
}) {
  const { t } = useTranslation();
  const feed = useSchoolFeed(ctx);
  const news = sim.has('noschool') ? [] : feed.news;
  const coming = sim.has('noschool') ? [] : feed.coming;
  if (news.length === 0 && coming.length === 0) return null;
  return (
    <section className={`${BLOCK} ${className}`}>
      <CardHead
        title={t('studentHome.glass.school.title')}
        to="/events"
        label={t('studentHome.glass.school.all')}
      />
      {news.length > 0 && (
        <ul
          className={`grid grid-cols-1 gap-2 ${news.length > 1 ? 'sm:grid-cols-2' : ''} ${coming.length > 0 ? 'mb-2' : ''}`}
        >
          {news.map((a) => (
            <li key={a.id} className="min-w-0">
              <Link
                to="/announcements"
                className="flex items-center gap-3 rounded-[18px] bg-[#F5F6F9] px-3 py-2.5 transition-colors hover:bg-[#EEF0F4]"
              >
                <span
                  className="flex size-9 shrink-0 items-center justify-center rounded-[12px]"
                  style={{ backgroundColor: tones.pink.soft, color: tones.pink.vivid }}
                >
                  <MegaphoneIcon size={17} weight="fill" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[14px] leading-5 font-semibold text-ink">
                    {a.title}
                  </span>
                  <span className="block truncate text-[12.5px] leading-[18px] text-muted">
                    {[a.author, feed.ago(a.publishedAt)].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <StatusPill tone="blue" dot size="xs">
                  {t('studentHome.glass.school.news')}
                </StatusPill>
              </Link>
            </li>
          ))}
        </ul>
      )}
      {coming.length > 0 && (
        // as many in a row as the width takes, the last row stretched; a card alone in its row (wider
        // than 28rem) lays out in a line: the date, what and where, how soon
        <div className="flex flex-wrap gap-2">
          {coming.map((e) => {
            const look = EVENT_LOOK[e.kind];
            const tint = tones[look.tone];
            const Icon = look.icon;
            return (
              <Link
                key={e.id}
                to="/events"
                className="@container relative isolate min-w-0 grow basis-[230px] overflow-hidden rounded-[22px] ring-2 ring-white transition-transform duration-300 ring-inset hover:-translate-y-0.5"
              >
                <span
                  aria-hidden
                  className="absolute inset-0 -z-10"
                  style={{ backgroundImage: ground(look.tone) }}
                />
                <span className="flex h-full min-h-[140px] flex-col p-3.5 @md:min-h-0 @md:flex-row @md:items-center @md:gap-4">
                  <span className="flex items-start justify-between gap-2 @md:contents">
                    <span className="flex h-[52px] w-12 shrink-0 flex-col items-center justify-center rounded-[14px] bg-white leading-none">
                      <span
                        className="font-display text-[20px] font-semibold"
                        style={{ color: tint.ink }}
                      >
                        {Number(e.startsOn.slice(8))}
                      </span>
                      <span className="mt-1 text-[11px] font-medium text-muted">
                        {weekdayShort(e.startsOn)}
                      </span>
                    </span>
                    <span className="max-w-[60%] min-w-0 @md:order-last @md:max-w-none @md:shrink-0">
                      <OnColorProvider value>
                        <StatusPill tone={look.tone} size="xs" className="max-w-full truncate">
                          {feed.until(e.startsOn, e.endsOn)}
                        </StatusPill>
                      </OnColorProvider>
                    </span>
                  </span>
                  <span className="mt-auto min-w-0 pt-3 @md:mt-0 @md:flex-1 @md:pt-0">
                    <span className="line-clamp-2 text-[14px] leading-5 font-semibold text-ink">
                      {e.title}
                    </span>
                    <span
                      className="mt-0.5 flex min-w-0 items-center gap-1 text-[12px] leading-[18px]"
                      style={{ color: tint.ink }}
                    >
                      <Icon
                        size={13}
                        weight="fill"
                        aria-hidden
                        color={tint.vivid}
                        className="shrink-0"
                      />
                      <span className="truncate">
                        {[e.atTime, e.place].filter(Boolean).join(' · ') ||
                          dayMonthShort(e.startsOn)}
                      </span>
                    </span>
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// —— the page ——

export function Glass2Home({ ctx }: { ctx: StudentContext }) {
  const sim = useSim();
  const day = useRef<HTMLDivElement>(null);
  const top = useStickyTop(day);
  return (
    <div className="grid grid-cols-1 gap-2 xl:grid-cols-12">
      <Situations ctx={ctx} className="xl:col-span-12" />
      <ProfileHeader ctx={ctx} className="xl:col-span-12" />
      <div ref={day} className="min-w-0 xl:sticky xl:col-span-5 xl:self-start" style={{ top }}>
        <GlassDay ctx={ctx} frame="rounded-[32px] bg-white p-4 md:p-6" />
      </div>
      {/* beside the lessons: a plain stack — a block of the moment comes or goes, the others close up */}
      <div className="flex min-w-0 flex-col gap-2 xl:col-span-7">
        <TodoBoard ctx={ctx} sim={sim} />
        <NewGrades ctx={ctx} sim={sim} />
        <School ctx={ctx} sim={sim} />
      </div>
    </div>
  );
}
